import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';
import {
    createOfflineSessionToken,
    getOfflineLoginErrorMessage,
    isOfflineSessionToken,
    parseOfflineSessionToken,
    saveOfflineLoginLease,
    validateOfflineLogin,
} from '../utils/offlineAuth';
import { clearTenantScopedClientState } from '../utils/tenantSessionStorage';
import { clearUploadTicket, primeUploadTicket } from '../api';
import archonQueryClient from '../config/queryClient';
import { clearAllTourCompletionFlags } from '../components/onboarding/productTourStorage';
import {
  warmupBackend,
  bootstrapGet,
  bootstrapPost,
  LOGIN_TIMEOUT_MS,
} from '../utils/bootstrapApi';
import {
  beginAuthBootstrap,
  endAuthBootstrap,
  subscribeForceLogout,
  isJwtExpired,
} from '../utils/authSession';

import { cacheMatchesToken, usernameFromToken } from '../utils/tokenIdentity';
import { lockSession, onSessionLock } from '../utils/sessionLock';
import SessionChangedBlocker from '../components/auth/SessionChangedBlocker';

const AuthContext = createContext(null);

const USER_CACHE_KEY = 'archon_user';

/**
 * El perfil cacheado, SÓLO si describe a quien firma el token que se envía.
 *
 * La identidad la fija el token; `archon_user` es una caché de su perfil. Sin
 * esta comprobación, una sesión abierta en otra pestaña dejaba a esta pintando
 * al usuario anterior mientras firmaba como el nuevo — doce minutos el
 * 16-ago-2026. Si no hay token, no se puede decodificar, o el `username` no es
 * el `sub`, la caché se descarta y el perfil se vuelve a resolver del servidor.
 */
function getCachedUser(tok = null) {
    try {
        const raw = localStorage.getItem(USER_CACHE_KEY);
        if (!raw) return null;
        const cached = JSON.parse(raw);
        const efectivo = tok !== null ? tok : localStorage.getItem('token');
        if (!cacheMatchesToken(cached, efectivo)) {
            try {
                localStorage.removeItem(USER_CACHE_KEY);
            } catch (_) { /* ignore */ }
            return null;
        }
        return cached;
    } catch {
        return null;
    }
}

/** JWT payload `sub` (username) — used only when /users/me fails temporarily (ARCHITECTURE R23; never revoke token on timeout). */
function usernameFromAccessToken(tok) {
    if (!tok || typeof tok !== 'string') return null;
    try {
        const mid = tok.split('.')[1];
        if (!mid) return null;
        const normalized = mid.replace(/-/g, '+').replace(/_/g, '/');
        const payload = JSON.parse(atob(normalized));
        return typeof payload?.sub === 'string' ? payload.sub : null;
    } catch {
        return null;
    }
}

/** Minimal profile so the app can render while /users/me retries in background (token stays valid). */
function placeholderUserFromToken(tok) {
    const username = usernameFromAccessToken(tok);
    if (!username) return null;
    return {
        username,
        id: null,
        email: null,
        full_name: username,
        role: '',
        photo_url: null,
        area: null,
        company_id: null,
        is_active: true,
        must_change_password: false,
    };
}

function buildOfflineFallbackUser(username, tenantSlug, snapshot = null) {
    if (snapshot && typeof snapshot === 'object') return snapshot;
    const normalizedUsername = String(username || '').trim();
    return {
        username: normalizedUsername,
        id: null,
        email: null,
        full_name: normalizedUsername || 'Offline User',
        role: '',
        photo_url: null,
        area: null,
        company_id: tenantSlug || null,
        is_active: true,
        must_change_password: false,
    };
}

function isMobileOrTabletDevice() {
    if (typeof navigator === 'undefined') return false;
    const ua = (navigator.userAgent || '').toLowerCase();
    const isTabletUA = /ipad|tablet|android(?!.*mobile)|kindle|silk|playbook/.test(ua);
    const isPhoneUA = /iphone|ipod|android.*mobile|windows phone|mobile/.test(ua);
    return isTabletUA || isPhoneUA;
}

function isBrowserOffline() {
    if (typeof navigator === 'undefined') return false;
    return navigator.onLine === false;
}

/** Read the stored token, purging a provably-expired JWT (e.g. shared browser, long idle). */
function readStoredTokenOrPurge() {
    let tok = null;
    try {
        tok = localStorage.getItem('token');
    } catch {
        return null;
    }
    if (!tok) return null;
    if (isOfflineSessionToken(tok)) return tok;
    if (isJwtExpired(tok)) {
        try {
            localStorage.removeItem('token');
            localStorage.removeItem(USER_CACHE_KEY);
            localStorage.removeItem('tenant_id');
        } catch (_) { /* ignore */ }
        return null;
    }
    return tok;
}

export const AuthProvider = ({ children }) => {
    // Token first: its initializer purges an expired JWT so user/status below see a clean slate.
    const [token, setToken] = useState(readStoredTokenOrPurge);
    const [user, setUser] = useState(() => {
        const tok = localStorage.getItem('token');
        if (!tok) return null;
        return getCachedUser() || placeholderUserFromToken(tok);
    });
    const [authStatus, setAuthStatus] = useState(() => {
        const tok = localStorage.getItem('token');
        if (!tok) return 'unauthenticated';
        return getCachedUser() || placeholderUserFromToken(tok) ? 'authenticated' : 'loading';
    });
    const [mustChangePassword, setMustChangePassword] = useState(false);
    const loginPasswordRef = useRef(null);
    //: El token con el que esta pestaña está trabajando. Un cambio en
    //: `localStorage` que no venga de aquí significa otra sesión.
    const tokenAlMontarRef = useRef(null);

    // `localStorage` es de todo el origen: otra pestaña que inicie sesión pisa
    // el token de ésta, y el interceptor de `api.js` lo lee en cada petición —
    // así que esta pestaña empezaría a firmar como otra persona sin enterarse.
    // Nadie escuchaba este evento; ésa era la ausencia que permitió el
    // incidente del 16-ago-2026.
    //
    // NO se recarga en silencio, a propósito: en un ERP eso puede tirar un
    // formulario a medio rellenar. Se bloquea, se avisa, y recarga la persona.
    const [sessionChanged, setSessionChanged] = useState(false);
    useEffect(() => {
        const desuscribir = onSessionLock(() => setSessionChanged(true));
        const alCambiarAlmacen = (e) => {
            if (e.key !== null && e.key !== 'token') return;
            const actual = (() => {
                try {
                    return localStorage.getItem('token');
                } catch {
                    return null;
                }
            })();
            // Sin sesión previa no hay nada que proteger: es un login normal.
            if (!tokenAlMontarRef.current) return;
            if (actual === tokenAlMontarRef.current) return;
            lockSession(actual ? 'token-replaced' : 'token-removed');
        };
        window.addEventListener('storage', alCambiarAlmacen);
        return () => {
            window.removeEventListener('storage', alCambiarAlmacen);
            desuscribir();
        };
    }, []);

    const refreshUser = useCallback(async () => {
        if (!token) return null;
        try {
            const response = await bootstrapGet('/users/me');
            const userData = response.data;
            // El ticket, ANTES de publicar el usuario.
            //
            // Iba detrás y sin `await`: la pantalla se pintaba mientras la
            // petición del ticket seguía en vuelo, los `<img src>` salían sin
            // `?t=` y recibían 404. El ticket llegaba un instante después y ya
            // no servía de nada — nada vuelve a pedir una imagen cuyo `src` ya
            // se resolvió, así que el hueco se quedaba hasta recargar.
            //
            // Esperarlo cuesta una petición y quita la carrera entera. Si falla,
            // `primeUploadTicket` devuelve null y la sesión sigue: un usuario sin
            // fotos es mejor que un usuario sin sesión.
            await primeUploadTicket();
            setUser(userData);
            setAuthStatus('authenticated');
            if (userData.must_change_password) {
                setMustChangePassword(true);
            }
            try {
                localStorage.setItem(USER_CACHE_KEY, JSON.stringify(userData));
            } catch (e) {
                // ignore quota / parse errors
            }
            return userData;
        } catch (error) {
            const status = error.response?.status;
            if (status === 401) {
                endAuthBootstrap();
                setUser(null);
                setToken(null);
                setAuthStatus('unauthenticated');
                localStorage.removeItem('token');
                localStorage.removeItem(USER_CACHE_KEY);
                localStorage.removeItem('axiomOnboardingComplete');
                localStorage.removeItem('tenant_id');
                clearTenantScopedClientState('auth-401');
                throw error;
            }
            // 403 al resolver el perfil: la identidad NO se pudo establecer.
            // No es «eres quien eras»: es «no pude comprobar quién eres», y
            // declarar sesión válida con el usuario cacheado es exactamente
            // como la interfaz mostró a un superadmin mientras firmaba como un
            // member. Se corta la sesión y se vuelve a entrar.
            if (status === 403) {
                endAuthBootstrap();
                setUser(null);
                setToken(null);
                setAuthStatus('unauthenticated');
                try {
                    localStorage.removeItem('token');
                    localStorage.removeItem(USER_CACHE_KEY);
                } catch (_) { /* ignore */ }
                clearTenantScopedClientState('auth-403');
                throw error;
            }
            const cached = getCachedUser();
            if (cached) {
                setUser(cached);
                setAuthStatus('authenticated');
            } else {
                // R23: timeouts / cold API / non-401 must NOT revoke the JWT — only 401 means bad credentials.
                const minimal = placeholderUserFromToken(token);
                if (minimal) {
                    setUser(minimal);
                    setAuthStatus('authenticated');
                } else {
                    setAuthStatus('loading');
                }
            }
            throw error;
        }
    }, [token]);

    useEffect(() => {
        if (token && isOfflineSessionToken(token)) {
            const cached = getCachedUser();
            const parsed = parseOfflineSessionToken(token);
            const fallbackUser = buildOfflineFallbackUser(parsed?.username, parsed?.tenant, cached);
            setUser(fallbackUser);
            setAuthStatus('authenticated');
            return;
        }
        // Expired JWT (shared browser / long idle): clean logout instead of optimistic render → 401 loop.
        if (token && isJwtExpired(token)) {
            endAuthBootstrap();
            setUser(null);
            setToken(null);
            setAuthStatus('unauthenticated');
            localStorage.removeItem('token');
            localStorage.removeItem(USER_CACHE_KEY);
            localStorage.removeItem('tenant_id');
            return;
        }
        if (token) {
            beginAuthBootstrap();
            localStorage.setItem('token', token);
            tokenAlMontarRef.current = token;
            if (!user) {
                const hydrated = getCachedUser() || placeholderUserFromToken(token);
                if (hydrated) {
                    setUser(hydrated);
                    setAuthStatus('authenticated');
                }
            }
            refreshUser().catch(() => {});
        } else {
            endAuthBootstrap();
            localStorage.removeItem('token');
            setAuthStatus('unauthenticated');
            localStorage.removeItem('axiomOnboardingComplete'); // Clear onboarding flag when no token
            localStorage.removeItem('tenant_id'); // Evita mantener un tenant cuando no hay sesión
        }
    }, [token, refreshUser]);

    // If we only have JWT `sub` (slow/failed /users/me), retry profile load once after a short delay.
    useEffect(() => {
        if (!token || user?.id != null) return undefined;
        if (!user?.username) return undefined;
        const tid = setTimeout(() => {
            refreshUser().catch(() => {});
        }, 4000);
        return () => clearTimeout(tid);
    }, [token, user?.id, user?.username, refreshUser]);

    const completeLoginWithToken = useCallback(
        async (accessToken, tenantSlug, options = {}) => {
            const { mustChangePassword: mustFlag } = options;
            const effectiveTenant =
                (tenantSlug && String(tenantSlug).trim().toLowerCase()) ||
                localStorage.getItem('tenant_id') ||
                null;
            if (effectiveTenant) {
                localStorage.setItem('tenant_id', effectiveTenant);
                localStorage.setItem('lastCompanyId', effectiveTenant);
            } else {
                localStorage.removeItem('tenant_id');
            }
            clearTenantScopedClientState('login-token');
            // Logout is declarative (no page reload) and the TanStack cache is a module
            // singleton, so entries from the previous session survive it. Dropping them
            // here — while only the login screen is mounted, so nothing refetches in a
            // burst — guarantees a session always starts on fresh server data instead of
            // repainting the previous user's master data (Job Positions, menus, lists).
            archonQueryClient.clear();
            loginPasswordRef.current = null;
            try {
                localStorage.removeItem(USER_CACHE_KEY);
            } catch (_) { /* ignore */ }
            beginAuthBootstrap();
            const minimal = placeholderUserFromToken(accessToken);
            setUser(minimal);
            setAuthStatus('authenticated');
            localStorage.setItem('token', accessToken);
            tokenAlMontarRef.current = accessToken;
            setToken(accessToken);
            setMustChangePassword(Boolean(mustFlag));
        },
        [],
    );

    const login = async (username, password, tenantSlug = null) => {
        const formData = new URLSearchParams();
        formData.append('username', username);
        formData.append('password', password);

        // Store password temporarily in memory for forced change flow
        loginPasswordRef.current = password;

        // Configurar headers
        const headers = { 'Content-Type': 'application/x-www-form-urlencoded' };

        // Si no llega tenantSlug, intenta usar el que ya está guardado (evita caer en Master DB por olvido)
        const effectiveTenant = tenantSlug || localStorage.getItem('tenant_id') || null;

        if (effectiveTenant) {
            headers['X-Tenant-ID'] = effectiveTenant;
            localStorage.setItem('tenant_id', effectiveTenant);
        } else {
            localStorage.removeItem('tenant_id');
        }
        // Force fresh project context per login/tenant to avoid cross-tenant stale selection.
        clearTenantScopedClientState('login-start');
        try {
            localStorage.removeItem(USER_CACHE_KEY);
        } catch (_) { /* ignore */ }
        setUser(null);

        // ARCHITECTURE.md R23 — Render cold-start hardening for login:
        //  1. Warm up `/status` first (cheap, public, no tenant) so the heavy POST does not
        //     have to wake dyno + PostgreSQL + tenant engine in one go.
        //  2. Use an extended per-call timeout (LOGIN_TIMEOUT_MS) ONLY for /token, so we
        //     survive a worst-case cold start without aborting at the global 60s cap.
        //  3. Retry with exponential backoff on retryable network/5xx errors.
        await warmupBackend();

        const tryOfflineLogin = async (requestError) => {
            // Keep desktop/laptop login behavior unchanged: offline fallback is only for mobile/tablet
            // and only when the browser is truly offline.
            if (!isMobileOrTabletDevice() || !isBrowserOffline()) {
                return false;
            }
            const status = requestError?.response?.status;
            const isNetworkLike =
                !status ||
                requestError?.code === 'ECONNABORTED' ||
                requestError?.code === 'ERR_NETWORK' ||
                (typeof requestError?.message === 'string' &&
                    (requestError.message.includes('Network') || requestError.message.includes('timeout')));
            if (!isNetworkLike) return false;

            const offline = await validateOfflineLogin({
                tenant: effectiveTenant,
                username,
                password,
            });
            if (!offline.ok) {
                throw new Error(getOfflineLoginErrorMessage(offline.reason));
            }

            const offlineUser = buildOfflineFallbackUser(username, effectiveTenant, offline.user_snapshot);
            const offlineToken = createOfflineSessionToken({ tenant: effectiveTenant, username });

            localStorage.setItem('token', offlineToken);
            localStorage.setItem(USER_CACHE_KEY, JSON.stringify(offlineUser));
            setUser(offlineUser);
            setToken(offlineToken);
            setAuthStatus('authenticated');
            setMustChangePassword(false);
            return true;
        };

        let response = null;
        try {
            response = await bootstrapPost('/token', formData, { headers });
        } catch (firstErr) {
            const usedOfflineLogin = await tryOfflineLogin(firstErr);
            if (usedOfflineLogin) return;
            throw firstErr;
        }

        if (!response.data || !response.data.access_token) {
            console.error('Respuesta inesperada en /token:', response.data);
            throw new Error('Login: respuesta inválida del servidor.');
        }

        const newToken = response.data.access_token;
        const offlineSnapshot =
            getCachedUser() ||
            placeholderUserFromToken(newToken) ||
            buildOfflineFallbackUser(username, effectiveTenant, null);
        saveOfflineLoginLease({
            tenant: effectiveTenant,
            username,
            password,
            userSnapshot: offlineSnapshot,
        }).catch(() => {});
        // Persist before setToken so axios (and any effect in the same tick) always sees the Bearer token.
        await completeLoginWithToken(newToken, effectiveTenant, {
            mustChangePassword: response.data.must_change_password,
        });
    };

    const logout = useCallback(() => {
        endAuthBootstrap();
        setUser(null);
        setToken(null);
        setAuthStatus('unauthenticated');
        setMustChangePassword(false);
        loginPasswordRef.current = null;
        localStorage.removeItem('token');
        localStorage.removeItem(USER_CACHE_KEY);
        clearUploadTicket();
        clearTenantScopedClientState('logout');
        clearAllTourCompletionFlags();
        localStorage.removeItem('tenant_id'); // Limpiar tenant al salir
        localStorage.removeItem('axiomOnboardingComplete');
    }, []);

    // Declarative logout (no hard reload). Clearing the token re-renders PrivateRoute, which
    // navigates to /login via React Router. A full page reload here, combined with optimistic
    // hydration, is what historically produced the /login ↔ /app loop.
    const forceLogoutAndRedirect = useCallback(() => {
        const currentPath =
            typeof window !== 'undefined'
                ? window.location.pathname + window.location.search
                : '';
        if (
            currentPath &&
            currentPath !== '/' &&
            currentPath !== '/login' &&
            currentPath !== '/app'
        ) {
            sessionStorage.setItem('redirectAfterLogin', currentPath);
        }
        logout();
    }, [logout]);

    useEffect(() => subscribeForceLogout(forceLogoutAndRedirect), [forceLogoutAndRedirect]);

    const value = {
        user,
        token,
        authStatus,
        login,
        completeLoginWithToken,
        logout,
        refreshUser,
        setUser,
        mustChangePassword,
        setMustChangePassword,
        loginPasswordRef,
        sessionChanged,
        //: La identidad que se ESTÁ ENVIANDO, derivada del token y no de la
        //: caché. Es lo que debe pintarse en pantalla: si alguna vez vuelve a
        //: divergir, se ve en el primer minuto en vez de a las cinco horas.
        tokenUsername: usernameFromToken(token),
    };

    return (
        <AuthContext.Provider value={value}>
            {sessionChanged && <SessionChangedBlocker />}
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    return useContext(AuthContext);
};

export { warmupBackend, LOGIN_TIMEOUT_MS };